// Create / edit a practical exercise: title, instructions, whether a
// submission must carry an evidence link and/or a score (with a minimum),
// who validates (manual · trainer · manager · system) and — for system —
// the Phase 6 rule: which allow-listed platform event (GET /system-events)
// proves the work, optional payload conditions ("*" = any value) and the
// optional payload field that names the learner. Emits a PracticeInput whose
// system_rule is exactly {event_type, match?, actor_path?}; the caller posts
// or patches. Matching itself is the server's.
import { useT } from '../lib/i18n';
import React, { useState } from 'react';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/features/shared/components/ui/button';
import { Input } from '@/features/shared/components/ui/input';
import { Label } from '@/features/shared/components/ui/label';
import { Textarea } from '@/features/shared/components/ui/textarea';
import { getApiErrorMessage } from '@/lib/apiError';
import type { Practice, PracticeInput, SystemRule, VerificationKind } from '@/services/learningService';
import { useSystemEvents } from '../api/hooks';
import { verificationHelp, verificationLabel } from '../lib/completion';

const selectCls = 'w-full h-9 rounded-md border border-border bg-background px-2 text-sm';
const KINDS: VerificationKind[] = ['manual', 'trainer', 'manager', 'system'];

interface MatchRow { key: string; field: string; value: string; any: boolean }
const rowKey = () => Math.random().toString(36).slice(2);
const rowsFrom = (rule?: SystemRule | null): MatchRow[] =>
  Object.entries(rule?.match || {}).map(([field, value]) => ({ key: rowKey(), field, value: value === '*' ? '' : String(value ?? ''), any: value === '*' }));

/** The wire shape: `match` and `actor_path` only when set (the server rejects an event type it does not list). */
export function buildSystemRule(eventType: string, rows: MatchRow[], actorPath: string): SystemRule {
  const rule: SystemRule = { event_type: eventType };
  const match: Record<string, string> = {};
  rows.forEach((r) => { const f = r.field.trim(); if (f) match[f] = r.any ? '*' : r.value.trim(); });
  if (Object.keys(match).length) rule.match = match;
  if (actorPath) rule.actor_path = actorPath;
  return rule;
}

interface Props {
  initial?: Practice | null;
  onSave: (input: PracticeInput) => void;
  onCancel: () => void;
  busy?: boolean;
  idPrefix?: string;
  className?: string;
}

export const PracticeEditor: React.FC<Props> = ({ initial, onSave, onCancel, busy, idPrefix = 'pr', className }) => {
  const t = useT();
  const [f, setF] = useState({
    title: initial?.title || '', instructions: initial?.instructions || '',
    evidence_required: initial?.evidence_required ?? true, score_required: initial?.score_required ?? false,
    min_score: initial?.min_score != null ? String(initial.min_score) : '',
    verification_kind: (initial?.verification_kind || 'trainer') as VerificationKind,
  });
  const [rule, setRule] = useState({ event_type: initial?.system_rule?.event_type || '', rows: rowsFrom(initial?.system_rule), actor_path: initial?.system_rule?.actor_path || '' });
  const [err, setErr] = useState<string | null>(null);
  const isSystem = f.verification_kind === 'system';
  // The editor is a learning:manage surface; the catalog is only needed for the system kind.
  const events = useSystemEvents(isSystem);
  const catalog = events.data || [];
  const selected = catalog.find((e) => e.event_type === rule.event_type);
  const patchRow = (key: string, p: Partial<MatchRow>) => setRule((r) => ({ ...r, rows: r.rows.map((x) => (x.key === key ? { ...x, ...p } : x)) }));

  const setKind = (kind: VerificationKind) => {
    setF({ ...f, verification_kind: kind });
    if (kind !== 'system') setRule({ event_type: '', rows: [], actor_path: '' });
  };
  const setEvent = (eventType: string) => {
    const next = catalog.find((e) => e.event_type === eventType);
    // Keep free-text conditions; drop an actor field the new event does not offer.
    setRule((r) => ({ ...r, event_type: eventType, actor_path: r.actor_path && next?.actor_fields?.includes(r.actor_path) ? r.actor_path : '' }));
  };

  const save = () => {
    setErr(null);
    if (!f.title.trim()) return setErr(t('training:practice_err_title', 'Give the exercise a title.'));
    const min = f.min_score.trim() === '' ? null : Number(f.min_score);
    if (f.score_required && min != null && (!Number.isFinite(min) || min < 0 || min > 100)) return setErr(t('training:practice_err_min_score', 'Minimum score must be between 0 and 100.'));
    if (isSystem) {
      // Mirrors the server's 400: "a system-verified practice needs a system_rule with an allow-listed event_type".
      if (!rule.event_type.trim()) return setErr(t('training:practice_err_event', 'A system-verified exercise needs an allow-listed platform event.'));
      if (rule.rows.some((r) => !r.field.trim())) return setErr(t('training:practice_err_match_field', 'Each condition needs a payload field.'));
      if (rule.rows.some((r) => !r.any && !r.value.trim())) return setErr(t('training:practice_err_match_value', 'Give each condition a value, or allow any value.'));
    }
    const input: PracticeInput = {
      title: f.title.trim(), instructions: f.instructions.trim() || undefined,
      evidence_required: f.evidence_required, score_required: f.score_required,
      min_score: f.score_required ? min : null,
      verification_kind: f.verification_kind,
      system_rule: isSystem ? buildSystemRule(rule.event_type.trim(), rule.rows, rule.actor_path) : null,
    };
    onSave(input);
  };

  return (
    <div className={`space-y-3 ${className || ''}`} data-testid="practice-editor">
      <div className="grid gap-2 sm:grid-cols-2">
        <div className="space-y-1 sm:col-span-2">
          <Label htmlFor={`${idPrefix}-title`} className="text-xs">{t('training:practice_name', 'Exercise title')}</Label>
          <Input id={`${idPrefix}-title`} className="h-9" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder={t('training:practice_name_ph', 'e.g. Complete a supervised forklift run')} />
        </div>
        <div className="space-y-1 sm:col-span-2">
          <Label htmlFor={`${idPrefix}-instr`} className="text-xs">{t('training:practice_instructions', 'Instructions for the learner')}</Label>
          <Textarea id={`${idPrefix}-instr`} rows={3} value={f.instructions} onChange={(e) => setF({ ...f, instructions: e.target.value })} />
        </div>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" className="accent-orange-500" checked={f.evidence_required} disabled={busy} onChange={(e) => setF({ ...f, evidence_required: e.target.checked })} />
          {t('training:practice_evidence_required', 'Evidence link required')}
        </label>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" className="accent-orange-500" checked={f.score_required} disabled={busy} onChange={(e) => setF({ ...f, score_required: e.target.checked })} />
          {t('training:practice_score_required', 'Score required')}
        </label>
        {f.score_required && (
          <div className="space-y-1">
            <Label htmlFor={`${idPrefix}-min`} className="text-xs">{t('training:practice_min_score', 'Minimum score (0–100, optional)')}</Label>
            <Input id={`${idPrefix}-min`} className="h-9 w-32" type="number" min={0} max={100} value={f.min_score} disabled={busy} onChange={(e) => setF({ ...f, min_score: e.target.value })} />
          </div>
        )}
        <div className="space-y-1 sm:col-span-2">
          <Label htmlFor={`${idPrefix}-kind`} className="text-xs">{t('training:practice_verification', 'Who validates')}</Label>
          <select id={`${idPrefix}-kind`} className={selectCls} value={f.verification_kind} disabled={busy} onChange={(e) => setKind(e.target.value as VerificationKind)}>
            {KINDS.map((k) => <option key={k} value={k}>{verificationLabel(t, k)}</option>)}
          </select>
          <p className="text-[11px] text-muted-foreground" data-testid="verification-help">{verificationHelp(t, f.verification_kind)}</p>
        </div>

        {isSystem && (
          <fieldset className="sm:col-span-2 space-y-2 rounded-md border border-border p-2" data-testid="system-rule">
            <legend className="px-1 text-xs font-medium">{t('training:system_rule_title', 'Verified by platform activity')}</legend>
            <p className="text-[11px] text-muted-foreground">{t('training:system_rule_help', 'Pick the platform event that proves the learner did the work. The exercise is validated the moment a matching event is recorded for the learner after the session starts — nothing is submitted by hand.')}</p>
            <div className="space-y-1">
              <Label htmlFor={`${idPrefix}-event`} className="text-xs">{t('training:practice_event_type', 'Platform event')}</Label>
              <select id={`${idPrefix}-event`} className={selectCls} value={rule.event_type} disabled={busy || events.isLoading} onChange={(e) => setEvent(e.target.value)}>
                <option value="">{events.isLoading ? `${t('training:loading', 'Loading')}…` : t('training:system_event_pick', 'Choose an event…')}</option>
                {catalog.map((e) => <option key={e.event_type} value={e.event_type}>{e.label} · {e.source_service}</option>)}
              </select>
              {events.isError && <p className="text-[11px] text-red-600 dark:text-red-400" role="alert">{getApiErrorMessage(events.error, t('training:system_events_unavailable', 'The list of platform events is not available right now.'))}</p>}
              {selected && <p className="text-[11px] text-muted-foreground" data-testid="system-event-desc">{selected.description} · <span className="font-mono">{selected.event_type}</span></p>}
            </div>

            <div className="space-y-1">
              <div className="text-xs font-medium">{t('training:system_match_title', 'Only when (optional)')}</div>
              {rule.rows.map((r, i) => (
                <div key={r.key} className="flex flex-wrap items-center gap-2" data-testid={`match-row-${i}`}>
                  <Input list={`${idPrefix}-hints`} aria-label={t('training:system_match_field', 'Field {{n}}', { n: i + 1 })} className="h-8 w-40" value={r.field} disabled={busy} placeholder={t('training:system_match_field_ph', 'payload field')} onChange={(e) => patchRow(r.key, { field: e.target.value })} />
                  <span className="text-xs text-muted-foreground" aria-hidden>=</span>
                  <Input aria-label={t('training:system_match_value', 'Value {{n}}', { n: i + 1 })} className="h-8 w-40" value={r.any ? '' : r.value} disabled={busy || r.any} placeholder={r.any ? t('training:system_match_any', 'Any value') : ''} onChange={(e) => patchRow(r.key, { value: e.target.value })} />
                  <label className="flex items-center gap-1 text-xs cursor-pointer">
                    <input type="checkbox" className="accent-orange-500" checked={r.any} disabled={busy} aria-label={t('training:system_match_any_n', 'Any value {{n}}', { n: i + 1 })} onChange={(e) => patchRow(r.key, { any: e.target.checked })} />
                    {t('training:system_match_any', 'Any value')}
                  </label>
                  <button type="button" aria-label={t('training:system_match_remove', 'Remove condition {{n}}', { n: i + 1 })} className="text-muted-foreground hover:text-red-500 disabled:opacity-50" disabled={busy} onClick={() => setRule((x) => ({ ...x, rows: x.rows.filter((y) => y.key !== r.key) }))}><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
              <datalist id={`${idPrefix}-hints`}>{(selected?.match_hints || []).map((h) => <option key={h} value={h} />)}</datalist>
              <Button size="sm" variant="outline" disabled={busy} onClick={() => setRule((x) => ({ ...x, rows: [...x.rows, { key: rowKey(), field: '', value: '', any: false }] }))} data-testid={`${idPrefix}-add-match`}>
                <Plus className="mr-1 h-4 w-4" />{t('training:system_match_add', 'Add condition')}
              </Button>
              <p className="text-[11px] text-muted-foreground">{t('training:system_match_help', 'Narrow the event by its payload, e.g. kind = task. "Any value" only requires the field to be present.')}</p>
            </div>

            <div className="space-y-1">
              <Label htmlFor={`${idPrefix}-actor`} className="text-xs">{t('training:system_actor', 'Field that names the learner (optional)')}</Label>
              <select id={`${idPrefix}-actor`} className={selectCls} value={rule.actor_path} disabled={busy || !selected} onChange={(e) => setRule((x) => ({ ...x, actor_path: e.target.value }))}>
                <option value="">{t('training:system_actor_default', "Default — the event's actor")}</option>
                {(selected?.actor_fields || []).map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
              <p className="text-[11px] text-muted-foreground">{t('training:system_actor_help', 'The payload field that must equal the learner. Keep the default unless the event names the learner in a specific field.')}</p>
            </div>
          </fieldset>
        )}
      </div>
      {err && <div role="alert" className="text-xs text-red-600 dark:text-red-400">{err}</div>}
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={onCancel} disabled={busy}>{t('training:cancel', 'Cancel')}</Button>
        <Button size="sm" onClick={save} disabled={busy} data-testid={`${idPrefix}-save`}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t('training:save', 'Save')}</Button>
      </div>
    </div>
  );
};

export default PracticeEditor;
